/**
 * Service Unavailable Page
 *
 * Feature: 001-frontend-integration
 * Task: T022
 * Description: Displays when backend is unreachable with 30-second auto-retry
 * Constitution: v1.11.0
 * Author: Peter Dobler
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';

const RETRY_INTERVAL_MS = 30000; // 30 seconds per FR-024 clarification

export const ServiceUnavailable: React.FC = () => {
  const navigate = useNavigate();
  const [retryCount, setRetryCount] = useState(0);
  const [secondsUntilRetry, setSecondsUntilRetry] = useState(30);
  const [isRetrying, setIsRetrying] = useState(false);

  const checkBackendHealth = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiClient.get('/health', { timeout: 5000 });
      return response.status === 200;
    } catch {
      return false;
    }
  }, []);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    const isHealthy = await checkBackendHealth();
    setIsRetrying(false);

    if (isHealthy) {
      // Backend is back online, navigate to dashboard
      navigate('/dashboard', { replace: true });
    } else {
      setRetryCount((prev) => prev + 1);
      setSecondsUntilRetry(30);
    }
  }, [checkBackendHealth, navigate]);

  // Auto-retry countdown timer
  useEffect(() => {
    const countdownTimer = setInterval(() => {
      setSecondsUntilRetry((prev) => {
        if (prev <= 1) {
          return 30; // Reset after reaching 0
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, []);

  // Auto-retry when countdown reaches 0
  useEffect(() => {
    if (secondsUntilRetry === 30 && retryCount > 0) {
      handleRetry();
    }
  }, [secondsUntilRetry, retryCount, handleRetry]);

  // Initial auto-retry setup
  useEffect(() => {
    const retryTimer = setInterval(() => {
      handleRetry();
    }, RETRY_INTERVAL_MS);

    return () => clearInterval(retryTimer);
  }, [handleRetry]);

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900 dark:to-gray-800'>
      <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center'>
        {/* Status Icon */}
        <div className='mb-6'>
          <div className='w-20 h-20 mx-auto bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center'>
            <svg
              className='w-10 h-10 text-orange-500 dark:text-orange-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className='text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2'>Service Unavailable</h1>

        {/* Message */}
        <p className='text-gray-600 dark:text-gray-400 mb-6'>
          We're having trouble connecting to the server. This is usually temporary.
        </p>

        {/* Status Info */}
        <div className='bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6'>
          <div className='flex justify-between items-center text-sm'>
            <span className='text-gray-500 dark:text-gray-400'>Retry attempts:</span>
            <span className='font-medium text-gray-700 dark:text-gray-200'>{retryCount}</span>
          </div>
          <div className='flex justify-between items-center text-sm mt-2'>
            <span className='text-gray-500 dark:text-gray-400'>Next retry in:</span>
            <span className='font-medium text-orange-600 dark:text-orange-400'>{secondsUntilRetry}s</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-6'>
          <div
            className='bg-orange-500 h-2 rounded-full transition-all duration-1000 ease-linear'
            style={{ width: `${((30 - secondsUntilRetry) / 30) * 100}%` }}
          />
        </div>

        {/* Manual Retry Button */}
        <button
          onClick={handleRetry}
          disabled={isRetrying}
          className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
            isRetrying
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isRetrying ? (
            <span className='flex items-center justify-center gap-2'>
              <svg className='animate-spin h-5 w-5' viewBox='0 0 24 24'>
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                  fill='none'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                />
              </svg>
              Checking connection...
            </span>
          ) : (
            'Retry Now'
          )}
        </button>

        {/* Help Text */}
        <p className='text-xs text-gray-400 dark:text-gray-500 mt-4'>
          If this problem persists, please contact your system administrator.
        </p>
      </div>
    </div>
  );
};

export default ServiceUnavailable;
